/*
  <aw-slideshow> — hero slideshow (sections/aw-slideshow.liquid).
  Crossfades slides, autoplays, pauses on hover/focus or when the tab is hidden,
  supports arrows, dots, keyboard and touch swipe.
*/
if (!customElements.get('aw-slideshow')) {
  customElements.define(
    'aw-slideshow',
    class AwSlideshow extends HTMLElement {
      connectedCallback() {
        this.slides = [...this.querySelectorAll('.aw-slide')];
        this.dots = [...this.querySelectorAll('.aw-slideshow__dot')];
        this.index = 0;
        if (this.slides.length < 2) return;

        const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        this.autoplay = this.dataset.autoplay === 'true' && !reduceMotion;
        if (!this.autoplay) this.removeAttribute('data-autoplay');
        this.delay = (parseFloat(this.dataset.speed) || 6) * 1000;

        this.querySelector('[data-prev]')?.addEventListener('click', () => this.go(this.index - 1));
        this.querySelector('[data-next]')?.addEventListener('click', () => this.go(this.index + 1));
        this.dots.forEach((dot) => dot.addEventListener('click', () => this.go(Number(dot.dataset.go))));

        this.addEventListener('mouseenter', () => this.pause());
        // Deferred a tick so :hover / focus have updated before play() checks them
        this.addEventListener('mouseleave', () => setTimeout(() => this.play()));
        // Keyboard focus pauses; a tap or click on a dot/arrow does not (it would stop autoplay for good)
        this.addEventListener('focusin', (event) => {
          if (event.target.matches(':focus-visible')) this.pause();
        });
        this.addEventListener('focusout', () => setTimeout(() => this.play()));
        document.addEventListener('visibilitychange', () => (document.hidden ? this.pause() : this.play()));

        this.addEventListener('keydown', (event) => {
          if (event.key === 'ArrowLeft') this.go(this.index - 1);
          if (event.key === 'ArrowRight') this.go(this.index + 1);
        });

        // Horizontal swipe on touch screens
        let startX = null;
        this.addEventListener('pointerdown', (event) => {
          if (event.pointerType !== 'mouse') startX = event.clientX;
        });
        this.addEventListener('pointerup', (event) => {
          if (startX === null) return;
          const distance = event.clientX - startX;
          if (Math.abs(distance) > 40) this.go(this.index + (distance < 0 ? 1 : -1));
          startX = null;
        });

        this.play();
      }

      disconnectedCallback() {
        clearTimeout(this.timer);
      }

      go(target) {
        const count = this.slides.length;
        const next = (target + count) % count;
        if (next === this.index) return;

        this.slides[this.index].classList.remove('is-active');
        this.slides[this.index].setAttribute('aria-hidden', 'true');
        this.slides[next].classList.add('is-active');
        this.slides[next].removeAttribute('aria-hidden');

        this.dots.forEach((dot, i) => {
          dot.classList.toggle('is-active', i === next);
          if (i === next) dot.setAttribute('aria-current', 'true');
          else dot.removeAttribute('aria-current');
        });
        // Restart the progress-bar animation on the new dot
        const bar = this.dots[next]?.querySelector('span');
        if (bar) {
          bar.style.display = 'none';
          void bar.offsetWidth;
          bar.style.display = '';
        }

        this.index = next;
        this.remaining = this.delay;
        this.schedule();
      }

      /*
        The progress bar (CSS animation) and this timer run for the same time. Pausing
        freezes both where they are; playing resumes both with the time that was left,
        so the bar always fills completely just as the next slide arrives.
      */
      schedule() {
        clearTimeout(this.timer);
        if (!this.autoplay || this.paused) return;
        if (!(this.remaining > 0)) this.remaining = this.delay;
        this.startedAt = Date.now();
        this.timer = setTimeout(() => this.go(this.index + 1), this.remaining);
      }

      pause() {
        if (this.paused) return;
        this.paused = true;
        this.classList.add('is-paused');
        clearTimeout(this.timer);
        if (this.startedAt) this.remaining = Math.max(0, this.remaining - (Date.now() - this.startedAt));
      }

      play() {
        if (this.paused === false) return;
        // Still hovered, or a keyboard user is still inside the slideshow
        if (this.matches(':hover') || this.querySelector(':focus-visible')) return;
        this.paused = false;
        this.classList.remove('is-paused');
        this.schedule();
      }
    }
  );
}
